import {
  AfterViewInit,
  Component, ElementRef,
  HostListener,
  Input, NgZone,
  OnInit, OnDestroy,
  ViewChild
} from '@angular/core';
import {Host, Menu, MenuGroup, MenuItem} from "../../../../common";
import {Cart} from "../../../../common";

export class FlatMenuGroupEntry {
  name: string;
  title: string;
  slug: string;
  items: MenuItem[];
}

@Component({
  selector: 'flat-menu',
  standalone: false,
  template: `
    <flat-menu-breadcrumb class="mt-3" [groups]="groups" [selected]="activeGroup"
                          (onSelect)="selectGroup($event)" *ngIf="groups.length > 1">
    </flat-menu-breadcrumb>
    <div class="cards mt-3" #groupRoot>
      <div *ngFor="let group of groups">
        <h1 class="h4 menu-group" [id]="'g' + group.slug">{{ group.title }}</h1>
        <flat-menu-item *ngFor="let item of group.items" id="#item{{ item.id }}"
                        [host]="host" [item]="item" (requestExpand)="onRequestExpand($event)"></flat-menu-item>
      </div>
    </div>
    <drawer [open]="drawerOpen" (close)="closeDrawer()">
        <menu-item [menu]="menu" [cart]="cart" [item]="expandedItem" *ngIf="expandedItem != null" (entryAdded)="closeDrawer()"></menu-item>
    </drawer>`
})
export class FlatMenuComponent implements OnInit, AfterViewInit, OnDestroy {

  @Input() host: Host;
  @Input() cart: Cart;
  @Input() menu: Menu;

  @ViewChild('groupRoot') groupRoot: ElementRef<HTMLDivElement>;

  groups: FlatMenuGroupEntry[] = [];
  drawerOpen: boolean = false;
  activeGroup: string = null;
  expandedItem: MenuItem = null;

  // resizeObserver observes resizing of t4dmain
  // @ts-ignore
  resizeObserver: ResizeObserver = null;

  // IntersectionObserver observes when elements enter or exit the view
  intersectionObserver: IntersectionObserver;

  constructor(private zone: NgZone) {}

  ngOnInit(): void {
    this.recurseGroups([this.menu.root]);
    this.hashChangeHandler(null); // force hash to be evaluated
  }

  ngAfterViewInit() {
    // @ts-ignore because it doesn't know ResizeObserver
    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        this.initIntersectionObserver();
        }
    });
    this.resizeObserver.observe(document.getElementById('t4dmain'));
  }

  ngOnDestroy(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  debugCreateVisualizer(obsMarginTop, obsMarginBottom) {
    // visualizes the area sensitive to the scroll observer
    const t4dmain = document.getElementById('t4dmain'),
          viz = document.createElement('div');
    viz.style.backgroundColor = '#FF000020';
    viz.style.position = (this.isMobile()) ? 'fixed': 'absolute';
    viz.style.top = -1*obsMarginTop + 'px';
    viz.style.bottom = -1*obsMarginBottom + 'px';
    viz.style.left = "0px";
    viz.style.right = "0px";
    if (!this.isMobile()) {
      t4dmain.parentElement.style.position = 'relative';
    }
    ((this.isMobile()) ? document.body : t4dmain.parentElement).appendChild(viz);
  }

  initIntersectionObserver() {
    const t4dmain = document.getElementById('t4dmain');

    let obsMarginTop,     // observer top margin
        obsMarginBottom;  // observer bottom margin

    // calculate observer margins. We consider a group to be active if its title
    // is in a 70px high area right below the title and breadcrumb, so we exclude
    // the rest of the scroll area with negative top and bottom margins
    if (this.isMobile()) {
      // mobile
      obsMarginTop = -90; // top menu + breadcrumb
      obsMarginBottom = (window.innerHeight + obsMarginTop - 70) * -1;
    } else {
      // desktop
      obsMarginTop = -60;
      obsMarginBottom = (t4dmain.offsetHeight + obsMarginTop - 70) * -1;
    }
    // the headings we are interested in (every <h1> below the group root)
    const headings = this.groupRoot.nativeElement.querySelectorAll('h1.menu-group');

    // initialize the IntersectionObserver
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = null;
    }
    this.intersectionObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          // explicitely run in zone so Angular gets notified of the update
          this.zone.run(() => {
            this.activeGroup = entry.target.id.substr(1);
            history.replaceState(null, '' ,'#g' + entry.target.id.substr(1));
          });
        }
      });
    }, {
      root: (this.isMobile()) ? null: t4dmain,
      threshold: 0.1,
      rootMargin: obsMarginTop.toString() + 'px 0px ' + obsMarginBottom.toString() + 'px 0px'
    });
    headings.forEach((el) => {
      this.intersectionObserver.observe(el);
    });
  }

  @HostListener('window:hashchange', ['$event'])
  hashChangeHandler($e) {
    if (location.hash.startsWith('#i')) {
      this.expandedItem = this.menu.itemsBySlug[location.hash.substr(2)];
      this.drawerOpen = !!(this.expandedItem);
    } else if (location.hash.startsWith('#g')) {
      this.selectGroup(location.hash.substr(2));
      setTimeout(() => {this.scrollToGroup(this.activeGroup, false)})
    } else {
      this.expandedItem = null;
      this.drawerOpen = false;
    }
  }

  selectGroup(slug: string) {
    this.activeGroup = slug;
    history.replaceState(null, '' ,'#g' + slug);
    this.scrollToGroup(slug);
  }

  isMobile() {
      // determine whether we are running on mobile, by looking at
      // the overflowY style of #t4dmain
      const t4dmain = document.getElementById('t4dmain'),
            style = getComputedStyle(t4dmain);
      return style.overflowY != 'auto';
  }

  getScrollContainer() {
    return (this.isMobile()) ? document.documentElement : document.getElementById('t4dmain');
  }

  scrollToGroup(slug, smooth = true) {
    const el = document.getElementById('g' + slug),
          elTop = el?.getBoundingClientRect().top,
          mainTop = document.getElementById('t4dmain').getBoundingClientRect().top,
          scrollContainer = this.getScrollContainer(),
          breadcrumbHeight = 46,
          scrollTop = scrollContainer?.scrollTop;

    if (!el) {
      // element not yet rendered, ignore
      return;
    }

    let target = 0;

    if (this.isMobile()) {
      // on mobile, we scroll the document itself, so we just need
      // to calculate the distance between the element and the top
      // of t4dmain (mainTop includes the current scroll position)
      target = elTop - mainTop - breadcrumbHeight;
    } else {
      // on desktop, we also need to take into account the current
      // scroll position of the scrollContainer, as mainTop is now
      // independent of the scroll position
      target = elTop - mainTop - breadcrumbHeight + scrollTop;
    }
    scrollContainer.scrollTo({top: target, left: 0, behavior: (smooth) ? 'smooth' : 'auto'});
  }

  recurseGroups(groups: MenuGroup[]): void {
    groups.forEach((group) => {
      if (group.items().length > 0) {
        this.groups.push({
          'name': group.name,
          'title': this.getGroupTitle(group),
          'slug': group.slug,
          'items': group.items(),
        });
      }
      this.recurseGroups(group.children());
    });
  }

  getGroupTitle(group: MenuGroup): string {
    if (group == this.menu.root) {
      return group.name;
    }
    let parent = this.menu.groups[group.parent];
    let path: string[] = [group.name];
    while (parent != this.menu.root) {
      path.push(parent.name);
      parent = this.menu.groups[parent.parent];
    }
    return path.reverse().join(' / ');
  }

  onRequestExpand($item) {
    this.expandedItem = $item;
    this.drawerOpen = true;
    history.pushState(null, '' ,'#i' + $item.slug);
  }

  closeDrawer() {
    this.drawerOpen = false;
    history.pushState(null, '' ,'#')
  }
}
